import { catchError, firstValueFrom, from, map, mergeMap } from 'rxjs';
import { RECAPTCHA_ACTION } from '../../authentication/constant/recaptcha';
import Authorization from '../../authentication/models/authorization';
import ErrorMapping from '../mappings/error.mapping';
import { Quotation, Quote } from '../models/quotation';
import QuotationAffiliate from '../models/quotation-affiliate';
import QuotationPlan from '../models/quotation-plan';
import QuotationProvider from '../providers/quotation-provider';
import Banks from '../models/bank';
import { Zone } from '../models/zone';
import { Profiling } from '../models/profiling';
import { ProfilingPostData } from '../interfaces/profiling-post-data';
import { PlanInfo } from '../models/plan-info';
import { Remarketing } from '../models/remarketing';
import SessionProvider from 'domains/core-base/authentication/providers/session-provider';
import QuotationMapping from '../mappings/quotation-mapping';
import {
  CashVehicleQuotePayload,
  CashVehicleQuoteResponse,
  RecommendedPlansRequest,
  RecommendedPlansResponse,
  RoutingPlansRequest,
  RoutingPlansResponse,
} from '../dtos/quotation-dto';
import {
  QuotationEssential,
  QuotationEssentialResponse,
} from '../models/quotation-essential';

class QuotationRepository {
  private auth: Authorization = Authorization.getInstance();

  constructor(
    private quotationProvider: QuotationProvider,
    private errorMapping: ErrorMapping,
    private sessionProvider: SessionProvider,
    private quotationMapping: QuotationMapping,
  ) {}

  async quote(quote: Quote): Promise<Quotation> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        quote,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(QuotationProvider.quote),
      map((result) =>
        this.quotationMapping.formatCreateQuoteResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  cashVehicleQuote = ({
    payload,
  }: CashVehicleQuotePayload): Promise<CashVehicleQuoteResponse> => {
    return firstValueFrom(
      from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
        map((recaptchaToken) => ({
          payload,
          recaptchaToken,
          token: this.auth.token,
        })),
        mergeMap(QuotationProvider.cashVehicleQuote),
        map(({ response }) =>
          this.quotationMapping.toCashVehicleQuoteResponse(response),
        ),
        catchError(this.errorMapping.newThrowError),
      ),
    );
  };

  async affiliate(
    data: QuotationAffiliate,
    options = {
      token: '',
    },
  ): Promise<Quotation> {
    const source$ = this.quotationProvider.affiliate(data, options).pipe(
      map((xhr) => xhr.response),
      catchError(this.errorMapping.throwError),
    );

    return await firstValueFrom(source$);
  }

  async getPlans(channel?: string): Promise<QuotationPlan[]> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        channel,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(QuotationProvider.getPlans),
      map((result) => this.quotationMapping.getQuotationPlans(result.response)),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async getBanks(): Promise<Banks[]> {
    const source$ = this.sessionProvider.getToken().pipe(
      map((sessionToken) => sessionToken),
      map((authorization) => ({
        authorization,
      })),
      mergeMap(QuotationProvider.getBanks),
      map((result) => this.quotationMapping.getBanks(result.response)),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async getZones(plate: string): Promise<Zone[]> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        recaptcha: recaptchaToken,
        plate,
      })),
      mergeMap(QuotationProvider.getZones),
      map((result) => this.quotationMapping.getZones(result.response)),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async profiling(
    data: ProfilingPostData,
    options = {
      token: '',
    },
  ): Promise<Profiling[]> {
    const source$ = this.quotationProvider.profiling(data, options).pipe(
      map((xhr) => xhr.response),
      catchError(this.errorMapping.throwError),
    );

    return await firstValueFrom(source$);
  }

  async getAllPlans(
    options = {
      token: '',
    },
  ): Promise<PlanInfo[]> {
    const source$ = this.quotationProvider.allPlans(options).pipe(
      map((xhr) => xhr.response),
      catchError(this.errorMapping.throwError),
    );

    return await firstValueFrom(source$);
  }

  async getRecommendedPlans(
    payload: RecommendedPlansRequest,
  ): Promise<RecommendedPlansResponse> {
    return firstValueFrom(
      from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
        map((recaptchaToken) => ({
          payload,
          recaptchaToken,
          token: this.auth.token,
        })),
        mergeMap(QuotationProvider.getRecommendedPlans),
        map(({ response }) =>
          this.quotationMapping.formatGetRecommendedPlansResponse(response),
        ),
        catchError(this.errorMapping.throwError),
      ),
    );
  }

  async getRoutingPlans(
    payload: RoutingPlansRequest,
  ): Promise<RoutingPlansResponse> {
    return firstValueFrom(
      from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
        map((recaptchaToken) => ({
          payload,
          recaptchaToken,
          token: this.auth.token,
        })),
        mergeMap(QuotationProvider.getRoutingPlans),
        map(({ response }) =>
          this.quotationMapping.formatGetRoutingPlansResponse(response),
        ),
        catchError(this.errorMapping.throwError),
      ),
    );
  }

  async getRemarketingQuote(requestId: string): Promise<Remarketing> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        requestId,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(QuotationProvider.getRemarketingQuote),
      map((result) =>
        this.quotationMapping.getRemarketingResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async getEssentialPlan(
    quote: QuotationEssential,
  ): Promise<QuotationEssentialResponse> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        quote,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(QuotationProvider.getEssentialPlan),
      map((result) =>
        this.quotationMapping.formatCreateQuoteEssentialResponse(
          result.response,
        ),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }
}

export const quotationRepository = new QuotationRepository(
  new QuotationProvider(),
  new ErrorMapping(),
  new SessionProvider(),
  new QuotationMapping(),
);
